import { generatePath, useOutletContext } from "react-router-dom";
import { useState } from "react";
import * as Event from "src/state/modules/event";
import * as Team from "src/state/modules/team";
import { Spacer } from "src/components/atoms/Spacer/Spacer";
import { OldGrid } from "src/components/atoms/OldGrid/OldGrid";
import { Title } from "src/components/atoms/Title/Title";
import { Button } from "src/components/atoms/Button/Button";
import { TabViewChooser } from "src/components/compounds/TabViewChooser/TabViewChooser";
import { ROUTES } from "src/utils/constants/routes";
import cn from "classnames";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import Styles from "./StatsPage.module.scss";
import StatsTable from "../../compounds/StatsTable/StatsTable";
import { mergeSeasonStatsForGrid } from "../Teams/stats-columns/common-columns";
import { useGetTeamEventStatsByIDQuery } from "src/state/modules/api";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";

type EventStatsPageProps = {
  team: Team.Team;
  event: Event.Event;
};

const StatsPage: React.FC = () => {
  const { team, event } = useOutletContext<EventStatsPageProps>();
  const views = [team.name, event.opponentTeam?.name];
  const [currentView, setCurrentView] = useState(views[0]);
  const { data, error, isLoading } = useGetTeamEventStatsByIDQuery({ teamID: team.id, eventID: event.id });

  if (isLoading) return <PageLoader1 />;

  const handleActive = (view: string) => setCurrentView(view);

  if (!data?.players || !data.opponents) return <NoGameStats team={team} event={event} />;
  if (!data.eventStats?.player_stats?.players) return <NoGameStats team={team} event={event} />;

  const activePlayers = currentView === views[0] ? data?.players : data?.opponents;

  return (
    <OldGrid className={cn(Styles.gameStatsPageContainer)}>
      <div className={cn(Styles["top-grid"])}>
        <div className={cn(Styles["header-row"])}>
          <Title className={cn(Styles["header-title"])} text="Game Stats" />
          <div className={cn(Styles["header-row-buttons"])}>&nbsp;</div>
        </div>
        <TabViewChooser
          className={cn(Styles.teamTabContainer)}
          options={views as string[]}
          active={currentView}
          onClick={handleActive}
        />
        <StatsTable grid={mergeSeasonStatsForGrid(activePlayers, data.eventStats.player_stats.players)} />
        <Spacer.Vertical />
      </div>
    </OldGrid>
  );
};
export default StatsPage;

// Per-game stats are only tallied once the game is final, so a live (or not-yet-scored) game lands
// here. Point the user to the Box Score tab, which carries live, up-to-the-minute stats instead.
function NoGameStats({ team, event }: { team: Team.Team; event: Event.Event }) {
  const isLive = event.gameData?.game_state === Event.GameState.LIVE;
  const boxScorePath = generatePath(ROUTES.teams.events.boxScore, { id: team.id, eid: event.id });

  return (
    <div className={cn(Styles.emptyWrap)}>
      <div className={cn(Styles.empty)}>
        <div className={cn(Styles.emptyIcon)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 20V10M10 20V4M16 20v-6M22 20H2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className={cn(Styles.emptyTitle)}>Game stats aren’t available yet</h3>
        <p className={cn(Styles.emptyBody)}>
          {isLive
            ? "Full game stats are tallied once the game goes final. While it’s still in progress, the Box Score tab has live, up-to-the-minute stats."
            : "Full game stats are tallied once the game goes final. In the meantime, the Box Score tab has the latest available stats."}
        </p>
        <Button type="filled" size="small" title="View Box Score" link={boxScorePath} />
      </div>
    </div>
  );
}
